import { cellAtWorld } from '../grid.ts';
import { dist2, iatan2, idiv, imax, imin, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy } from '../types.ts';
import type { Pt } from '../caza-types.ts';
import type { ElderRules, ElderState } from '../bote-types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { crowsAt, gnawHold, gnawTick, hitBoat, onHull } from './bote-boat.ts';

/**
 * The old howler (docs/sendas/barbaro.md, chapter 3, act 3, the test: «El aullador viejo»; the ground in
 * battle/bote-layout.ts). A storm overhead by night, lightning showing the whole sea at moments; the boat among four
 * floes joined to it by necks of ice, the Grey Wolf circling far off. The old howler, big and grizzled, the one who leads
 * when the Wolf isn't there: it bites, and every so often it leaps onto the hero, its shadow on the deck three seconds
 * before. The rule, the Wolf's in small: the deep water is darker, off two of the floes' tips at a time, and it moves.
 * Each time it loses a part of its life the howler looks three seconds toward the nearest dark tip (marked red), then
 * swims for it at the hero's pace; there it goes under and comes back healed, with the pack. Cut it off waiting at that
 * tip, or keeping on it: each blow takes a part of its pace, and enough of them and it's caught. Won with it down; lost
 * with the hero down, the boat broken (40 blows) or the howler back with its whole life.
 *
 * Cost: one nightmare of its own and the pack; the tips four points.
 */

const er = (s: BattleState): ElderRules | undefined => s.rules?.senda?.bote?.elder;
const es = (s: BattleState): ElderState | undefined => s.senda?.bote?.elder;

const fresh = (s: BattleState, r: ElderRules): ElderState => {
  // It comes up out of the dark water at the floe farthest from the hero's start, onto its tip.
  const p = r.tips[1] as Pt;
  const e = spawnEnemy(s, 'wolf', 1, false, 0, p.x, p.y, r.hp);
  return {
    id: e.id,
    phase: 0,
    until: 0,
    tip: -1,
    via: null,
    streak: 0,
    hits: 0,
    hpWas: e.hp,
    fleeAt: e.maxHp - mulBp(e.maxHp, r.fleeBp),
    mark: e.maxHp,
    cd: 0,
    leapAt: { x: 0, y: 0 },
    leapT: -1,
    leapNext: r.leapFirst,
    dark: r.dark[0] ?? 3,
    shiftAt: r.shift,
    flights: 0,
    cuts: 0,
    dives: 0,
    next: r.pack.first,
    crow: r.crows.first,
    gnawCd: r.boat.gnaw,
    full: -1,
    done: -1,
  };
};

/** The old howler's state, if this nightmare is it. */
export const elderOf = (s: BattleState, id: number): ElderState | undefined => {
  const w = es(s);
  return w && w.id === id ? w : undefined;
};

/** The dark tip nearest a point (−1: none dark). */
export const elderNearestDark = (r: ElderRules, mask: number, x: number, y: number): number => {
  let best = -1;
  let bd = 0x7fffffff;
  r.tips.forEach((t, k) => {
    if (!(mask & (1 << k))) return;
    const d = dist2(t.x, t.y, x, y);
    if (d < bd) {
      bd = d;
      best = k;
    }
  });
  return best;
};

/** The howler's pace swimming for the water, after `hits` blows on the way. */
export const elderPace = (r: ElderRules, hits: number): number => imax(idiv(r.speed, 3), r.speed - mulBp(r.speed, r.slowBp * hits));

const cut = (s: BattleState, r: ElderRules, w: ElderState, e: Enemy, how: number, cue: (c: string) => void): void => {
  w.phase = 4;
  w.until = s.tick + r.stun;
  w.cuts++;
  w.streak = 0;
  w.mark = e.hp;
  e.stun = imax(e.stun, r.stun);
  ev(s, { t: 'senda', kind: 'bCut', npc: '', x: e.x, y: e.y, n: how });
  cue(`cut${w.cuts}`);
};

export const elderTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = er(s);
  const st = s.senda;
  if (!r || !st) return;
  const bs = (st.bote ??= {});
  const w = (bs.elder ??= fresh(s, r));
  if (!s.started || w.done >= 0 || w.full >= 0) return;
  const e = s.enemies.find((x) => x.id === w.id);
  if (!e || e.hp <= 0) {
    w.done = s.tick;
    cue('elderDown');
    return;
  }
  const h = s.heroes.find((x) => x.downTicks === 0);
  // The dark water moves: two of the floes' tips at a time, by turns.
  if (s.waveT >= w.shiftAt) {
    w.shiftAt = s.waveT + r.shift;
    const i = r.dark.indexOf(w.dark);
    w.dark = r.dark[(i + 1) % r.dark.length] ?? w.dark;
    ev(s, { t: 'senda', kind: 'bDark', npc: '', x: 0, y: 0, n: w.dark });
  }
  if (w.cd > 0) w.cd--;
  // Its flight: a part of its life lost and it looks at the water.
  if (w.phase === 0 && e.hp <= w.fleeAt) {
    const k = elderNearestDark(r, w.dark, e.x, e.y);
    if (k >= 0) {
      w.phase = 1;
      w.until = s.tick + r.look;
      w.tip = k;
      w.leapT = -1;
      w.flights++;
      w.fleeAt = imax(1, w.fleeAt - mulBp(e.maxHp, r.fleeBp));
      const t = r.tips[k] as Pt;
      ev(s, { t: 'senda', kind: 'bLook', npc: '', x: t.x, y: t.y, n: k });
      cue(`look${w.flights}`);
    }
  } else if (w.phase === 1 && s.tick >= w.until) {
    w.phase = 2;
    w.hits = 0;
    w.hpWas = e.hp;
    // Across the boat it swims round the bow or the stern, whichever its tip is nearer.
    const t = r.tips[w.tip] as Pt;
    w.via = (t.x > 0) !== (e.x > 0) ? { x: 0, y: t.y < 0 ? r.boat.bow - 1800 : r.boat.stern + 1800 } : null;
    // A bound toward the water first, out of reach (never past the tip).
    const aim = w.via ?? t;
    const dx = aim.x - e.x;
    const dy = aim.y - e.y;
    const d = imax(1, isqrt(dx * dx + dy * dy));
    const b = imin(d - 800, r.bound);
    if (b > 0) {
      e.x += idiv(dx * b, d);
      e.y += idiv(dy * b, d);
    }
    ev(s, { t: 'senda', kind: 'bFlee', npc: '', x: e.x, y: e.y, n: w.tip });
  } else if (w.phase === 2) {
    if (e.hp < w.hpWas) w.hits++;
    w.hpWas = e.hp;
    const t = r.tips[w.tip] as Pt;
    if (w.hits >= r.caught) cut(s, r, w, e, 1, cue);
    else if (withinRange(e.x, e.y, t.x, t.y, 700)) {
      if (h && withinRange(h.x, h.y, t.x, t.y, r.block)) cut(s, r, w, e, 0, cue);
      else {
        // Into the dark water, under.
        w.phase = 3;
        w.until = s.tick + r.under;
        w.dives++;
        w.streak++;
        const d = r.deep[w.tip] as Pt;
        ev(s, { t: 'senda', kind: 'bDive', npc: '', x: t.x, y: t.y, n: w.tip });
        e.x = d.x;
        e.y = d.y;
        cue(`dive${w.dives}`);
      }
    }
  } else if (w.phase === 3 && s.tick >= w.until) {
    // Back up onto the tip, healed, the pack with it.
    const t = r.tips[w.tip] as Pt;
    e.x = t.x;
    e.y = t.y;
    // The dark water heals what it lost since it last came up or was cut off, and a little more.
    e.hp = imin(e.maxHp, imax(e.hp, w.mark) + mulBp(e.maxHp, r.healBp));
    w.mark = e.hp;
    w.fleeAt = imax(1, e.hp - mulBp(e.maxHp, r.fleeBp));
    w.phase = 0;
    w.hpWas = e.hp;
    for (let i = 0; i < r.raise; i++) spawnEnemy(s, 'wolf', 1, false, 0, t.x + ((i % 3) - 1) * 450, t.y + (i >> 1) * 400, r.wolfHp);
    ev(s, { t: 'senda', kind: 'bRise', npc: '', x: t.x, y: t.y, n: w.tip });
    cue(`rise${w.dives}`);
    if (e.hp >= e.maxHp && w.streak >= 2) {
      w.full = s.tick;
      ev(s, { t: 'senda', kind: 'bFull', npc: '', x: t.x, y: t.y });
      cue('full');
      return;
    }
  } else if (w.phase === 4 && s.tick >= w.until) {
    w.phase = 0;
    w.hpWas = e.hp;
  }
  // The leap: its shadow on the hero's spot, then down on it.
  if (w.phase === 0 && h) {
    if (w.leapT < 0 && s.waveT >= w.leapNext) {
      w.leapNext = s.waveT + r.leapEvery;
      w.leapAt = { x: h.x, y: h.y };
      w.leapT = s.tick + r.leapWarn;
      ev(s, { t: 'senda', kind: 'bLeapWarn', npc: '', x: h.x, y: h.y, n: r.leapR });
      cue('leap1');
    } else if (w.leapT >= 0 && s.tick >= w.leapT) {
      w.leapT = -1;
      e.x = w.leapAt.x;
      e.y = w.leapAt.y;
      ev(s, { t: 'senda', kind: 'bLeap', npc: '', x: e.x, y: e.y, n: r.leapR });
      if (withinRange(h.x, h.y, e.x, e.y, r.leapR)) damageHero(s, h, mulBp(h.stats.maxHp, r.leapBp), e, 'physical', false);
      if (onHull(r.boat, cellAtWorld(s, e.x, e.y))) hitBoat(s, r.leapHull, e.x, e.y);
    }
  }
  // The pack climbing out of the sea on the floes' outer sides, or up the gunwale (not at the tips: those are the
  // howler's); crows.
  if (s.waveT >= w.next) {
    w.next = s.waveT + rngRange(s.rng, r.pack.every[0], r.pack.every[1]);
    const k = rngInt(s.rng, r.floes.length + 1);
    const f = r.floes[k];
    const at = f ? { x: f.x + (f.x > 0 ? 900 : -900), y: f.y } : { x: rngInt(s.rng, 2) ? 900 : -900, y: (rngInt(s.rng, 5) - 2) * 1500 };
    const n = rngRange(s.rng, r.pack.n[0], r.pack.n[1]);
    for (let i = 0; i < n; i++) spawnEnemy(s, 'wolf', 1, false, 0, at.x + ((i % 2) * 2 - 1) * 250, at.y + (i - 1) * 450, r.wolfHp);
  }
  if (h && s.waveT >= w.crow) {
    w.crow = s.waveT + r.crows.every;
    crowsAt(s, r.crows.n, h.x + 7000, h.y - 4000, r.wolfHp);
  }
  w.gnawCd = gnawTick(s, r.boat, w.gnawCd);
};

/** The old howler moves and bites by its own rule; the gnawers at the hull (true: the act moved it). */
export const elderHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const r = er(s);
  const w = es(s);
  if (!r || !w) return false;
  if (e.id !== w.id) return e.stun > 0 ? false : gnawHold(s, r.boat, e);
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (w.phase === 3) {
    e.anim = 'idle';
    return true;
  }
  if (w.phase === 4 || e.stun > 0) return false;
  if (w.phase === 1) {
    // Looking at the water: its head toward the tip.
    const t = r.tips[w.tip] as Pt;
    e.facing = iatan2(t.y - e.y, t.x - e.x);
    e.anim = 'cast';
    return true;
  }
  if (w.phase === 2) {
    const t = w.via ?? (r.tips[w.tip] as Pt);
    e.anim = 'walk';
    if (stepToward(e, t.x, t.y, idiv(elderPace(r, w.hits), 12)) && w.via) w.via = null;
    return true;
  }
  // Crouched for the leap: it stands.
  if (w.leapT >= 0) {
    if (h && (h.x !== e.x || h.y !== e.y)) e.facing = iatan2(h.y - e.y, h.x - e.x);
    e.anim = 'cast';
    return true;
  }
  if (!h) {
    e.anim = 'idle';
    return true;
  }
  if (!withinRange(e.x, e.y, h.x, h.y, r.reach)) {
    e.anim = 'walk';
    stepToward(e, h.x, h.y, px(e));
    return true;
  }
  if (h.x !== e.x || h.y !== e.y) e.facing = iatan2(h.y - e.y, h.x - e.x);
  if (w.cd === 0) {
    w.cd = r.reload;
    e.anim = 'attack';
    e.animT = 0;
    damageHero(s, h, mulBp(h.stats.maxHp, r.biteBp), e, 'physical', false);
  } else if (e.anim !== 'attack' || e.animT > 6) e.anim = 'idle';
  return true;
};

/** The howler's life taken, of all of it. */
export const elderGoal = (s: BattleState): { done: number; need: number } => {
  const w = es(s);
  const e = w ? s.enemies.find((x) => x.id === w.id) : undefined;
  if (!w || !e) return { done: w && w.done >= 0 ? 1 : 0, need: 1 };
  return { done: e.maxHp - imax(0, e.hp), need: e.maxHp };
};

export const elderResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = es(s);
  if (!w) return null;
  if (w.full >= 0 && s.tick >= w.full + 24) return 'lost';
  return w.done >= 0 && s.tick >= w.done + 24 ? 'won' : null;
};
